<template>
    <ValidationErrors :show="showErrors"/>
    <Head :title="'Supplier Invoices - ' + fixture.title"/>
    <Audit v-if="openModal" @close="openModal = false" :open="openModal" :activity_logs="activity_logs" />
    <div class="w-full mb-6">
        <FixtureHeader :fixture="fixture"></FixtureHeader>
        <div class="space-y-6 lg:col-span-9">
            <section aria-labelledby="payment-details-heading">
                <form @submit.prevent="submit">
                    <div class="shadow sm:rounded-md sm:overflow-hidden">
                        <div class="bg-white px-4 p-6 space-y-6">
                            <div class="flex pb-5 border-b border-gray-200 justify-between items-center">
                                <h3 class="text-lg leading-6 font-bold text-gray-900">Supplier Invoices</h3>
                                <div class="flex text-right items-center">
                                    <Button @click="emitter.emit('openLinkFixtureSupplierInvoiceSlideout', fixture)"
                                            class="mr-2 shadow-md inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                        <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             stroke="currentColor" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                  stroke-width="2"
                                                  d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        Link Existing Supplier Invoice
                                    </Button>
                                    <Button @click="emitter.emit('openSupplierInvoiceSlideout', fixture)"
                                            class="shadow-md inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                        <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg"
                                             fill="none"
                                             viewBox="0 0 24 24"
                                             stroke="currentColor" aria-hidden="true">
                                            <path stroke-linecap="round" stroke-linejoin="round"
                                                  stroke-width="2"
                                                  d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                        </svg>
                                        Create New Supplier Invoice
                                    </Button>

                                    <search-filter :show-filter="false" v-model:value="form.search" class="w-1/2 max-w-md mr-4" @reset="reset"></search-filter>

                                </div>
                            </div>

                            <div class="flex flex-col mt-3 space-y-3">
                                <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                    <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                        <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                            <table class="min-w-full divide-y divide-gray-200">
                                                <thead class="bg-gray-50">
                                                <tr>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Invoice Number
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Supplier
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Received Date
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Amount
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        {{ this.fixture.primary_trading_details?.currency?.code ?? 'GBP' }} Value
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                                        Value Assigned To Fixture
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider">
                                                        Related Invoices
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider"> 
                                                        Related Fixtures
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider">
                                                        Invoiced %
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider">
                                                        Reviewed
                                                    </th>
                                                    <th scope="col"
                                                        class="px-6 py-3 text-center text-xs text-gray-500 uppercase tracking-wider">
                                                        Reviewed At
                                                    </th>
                                                    <th scope="col" class="relative px-6 py-3">
                                                        <span class="sr-only">Edit</span>
                                                    </th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                <tr v-if="supplier_invoices.data.length > 0"
                                                    v-for="(supplier_invoice, index) in supplier_invoices.data" :key="index"
                                                    :class="{'bg-gray-100': index % 2, 'bg-orange-100': supplier_invoice.amount_disputed > 0}"
                                                    class="hover:bg-gray-50 cursor-pointer">
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {{ supplier_invoice.invoice_number }}
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {{ supplier_invoice.supplier.name }}
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {{ moment(supplier_invoice.received_at, 'YYYY-MM-DD').format('DD MMM YYYY') }}
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {{ formatCurrency(supplier_invoice.amount, supplier_invoice.currency?.code ?? 'gbp', true) }}
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {{ formatCurrency(supplier_invoice.gbp_amount, this.fixture.primary_trading_details?.currency?.code ?? 'gbp', true) }}
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        {{ formatCurrency(supplier_invoice.pivot.amount, this.fixture.primary_trading_details?.currency?.code ?? 'gbp', true) }}
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        <div class="flex justify-center items-center gap-3">
                                                            <span class="underline text-pitch-purple text-gray-500"
                                                              @click="emitter.emit('openLinkSupplierInvoiceSlideout', supplier_invoice)">{{
                                                                supplier_invoice.related_invoices.length
                                                            }}</span>
                                                            <button @click="emitter.emit('openLinkSupplierInvoiceSlideout', supplier_invoice)" type="button"
                                                                    class="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                                <PlusSmIcon class="h-4 w-4 text-white" aria-hidden="true"/>
                                                            </button>
                                                            <Popper v-if="parseFloat(supplier_invoice.gbp_amount) !== parseFloat(supplier_invoice.total_amount)" :hover="true" class="cursor-pointer" placement="top" content="The total invoice amount has not been assigned across related invoices.">
                                                                <ExclamationCircleIcon
                                                                class="h-5 w-5 text-red-500"
                                                                aria-hidden="true"/>
                                                            </Popper>
                                                        </div>
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                        <div class="flex justify-center items-center gap-3">
                                                            <span class="underline text-pitch-purple text-gray-500"
                                                              @click="emitter.emit('openLinkSupplierInvoiceFixtureSlideout', supplier_invoice)">{{
                                                                supplier_invoice.related_fixtures.length
                                                            }}</span>
                                                            <button @click="emitter.emit('openLinkSupplierInvoiceFixtureSlideout', supplier_invoice)" type="button"
                                                                    class="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-pitch-purple hover:bg-dark-pitch-purple focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                                                <PlusSmIcon class="h-4 w-4 text-white" aria-hidden="true"/>
                                                            </button>
                                                            <Popper v-if="parseFloat(supplier_invoice.gbp_amount) !== parseFloat(supplier_invoice.total_fixture_linked_amount)" :hover="true" class="cursor-pointer" placement="top" content="The total invoice amount has not been assigned across fixtures.">
                                                                <ExclamationCircleIcon
                                                                class="h-5 w-5 text-red-500"
                                                                aria-hidden="true"/>
                                                            </Popper>
                                                        </div>
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-900">
                                                        <span class="flex items-center gap-2">

                                                        {{ supplier_invoice.invoiced_amount }}%
                                                        <CheckCircleIcon v-if="supplier_invoice.invoiced_amount <= 100" alt="Remove Folder"
                                                                          class="cursor-pointer w-4 h-4 text-green-400 hover:text-gray-500"></CheckCircleIcon>
                                                        <Popper v-else :hover="true" class="cursor-pointer" placement="top" content="The amount raised is over the expected amount for this supplier invoice, please check the invoices raised for this">
                                                        <XCircleIcon alt="Remove Folder"
                                                                          class="cursor-pointer w-4 h-4 text-red-400 hover:text-gray-500"></XCircleIcon>
                                                        </Popper>
                                                                                                                    </span>

                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-center items-center text-gray-900">
                                                        <span v-if="supplier_invoice.review_required == null"></span>
                                                        <span v-else-if="supplier_invoice.review_required" class="items-center">
                                                            <XCircleIcon class="w-4 h-4 text-red-400"></XCircleIcon>
                                                        </span>
                                                        <span v-else class="text-center">
                                                            <CheckCircleIcon alt="Remove Folder" class="w-4 h-4 text-green-400"></CheckCircleIcon>
                                                        </span>
                                                    </td>
                                                    <td class="px-6 py-4 whitespace-nowrap text-sm text-center items-center text-gray-900">
                                                        <span v-if="supplier_invoice.reviewed_at">
                                                            {{ moment(supplier_invoice.reviewed_at, 'YYYY-MM-DD').format('DD MMM YYYY') }}
                                                        </span>
                                                    </td>
                                                    <td class="px-6  py-4 whitespace-nowrap text-right text-sm flex">
                                                        <div v-if="!supplier_invoice.disable_email">
                                                            <MailIcon v-if="!supplier_invoice.email_sent" @click="emitter.emit('openEmailSupplierInvoiceSlideout', supplier_invoice)"
                                                                        class="w-4 h-4 mr-3"></MailIcon>
                                                            <MailOpenIcon v-else @click="emitter.emit('openEmailSupplierInvoiceSlideout', supplier_invoice)"
                                                                        class="w-4 h-4 mr-3"></MailOpenIcon>
                                                        </div>
                                                        <PencilAltIcon @click="emitter.emit('editSupplierInvoiceSlideout', supplier_invoice)"
                                                                       class="w-4 h-4 mr-3"></PencilAltIcon>
                                                        <TrashIcon @click="deleteSupplierInvoice(supplier_invoice.id)"
                                                                   class="w-4 h-4 text-red-500"></TrashIcon>
                                                    </td>
                                                </tr>
                                                <tr v-else>
                                                    <td colspan="9"
                                                        class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">
                                                        There are no supplier invoices available for your search criteria.
                                                    </td>
                                                </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                                <Pagination :links="supplier_invoices.links"/>
                            </div>

                            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </form>
            </section>
        </div>
    </div>
    <CreateSupplierInvoice/>
    <LinkExistingSupplierInvoice/>
    <LinkSupplierInvoiceToInvoice/>
    <LinkSupplierInvoiceToFixture/>
    <EmailSupplierInvoice/>
</template>


<script>
import moment from "moment";
import {
    ClipboardCopyIcon,
    PlusSmIcon,
    UsersIcon,
    InformationCircleIcon,
    ChevronDownIcon,
    CogIcon,
    PencilAltIcon,
    CheckCircleIcon,
    TrashIcon,
    MailIcon,
    MailOpenIcon,
    XCircleIcon,
    ExclamationCircleIcon
} from "@heroicons/vue/outline";
import Popper from "vue3-popper";
import {emitter} from "@/eventHub";
import pickBy from "lodash/pickBy";
import debounce from "lodash/debounce";
import { router } from '@inertiajs/vue3'
import mapValues from 'lodash/mapValues';
import Button from "@/Components/Button";
import Pagination from "@/Components/Pagination";
import Authenticated from "@/Layouts/Authenticated";
import {Link, Head, useForm} from "@inertiajs/vue3";
import SearchFilter from "@/Components/SearchFilter";
import FixtureHeader from "@/Components/FixtureHeader";
import ValidationErrors from "@/Components/ValidationErrors";
import CreateQuote from "@/Components/SlideOuts/CreateQuote";
import CreateExpense from "@/Components/SlideOuts/CreateExpense";
import DuplicateQuote from "@/Components/SlideOuts/DuplicateQuote";
import {Popover, PopoverButton, PopoverGroup} from "@headlessui/vue";
import EmailSupplierInvoice from "@/Components/SlideOuts/EmailSupplierInvoice";
import CreateSupplierInvoice from "@/Components/SlideOuts/CreateSupplierInvoice";
import LinkExistingSupplierInvoice from "@/Components/SlideOuts/LinkExistingSupplierInvoice";
import LinkSupplierInvoiceToInvoice from "@/Components/SlideOuts/LinkSupplierInvoiceToInvoice";
import LinkSupplierInvoiceToFixture from "@/Components/SlideOuts/LinkSupplierInvoiceToFixture";
import Audit from "@/Components/Modal/Audit.vue";


export default {
    layout: Authenticated,
    components: {
        Link,
        Head,
        Popper,
        Button,
        Popover,
        CogIcon,
        MailIcon,
        TrashIcon,
        UsersIcon,
        Pagination,
        PlusSmIcon,
        CreateQuote,
        XCircleIcon,
        MailOpenIcon,
        SearchFilter,
        PopoverGroup,
        FixtureHeader,
        PopoverButton,
        CreateExpense,
        DuplicateQuote,
        PencilAltIcon,
        ChevronDownIcon,
        CheckCircleIcon,
        ValidationErrors,
        ClipboardCopyIcon,
        EmailSupplierInvoice,
        CreateSupplierInvoice,
        InformationCircleIcon,
        ExclamationCircleIcon,
        LinkExistingSupplierInvoice,
        LinkSupplierInvoiceToInvoice,
        LinkSupplierInvoiceToFixture,
        Audit
    },
    props: {
        fixture: Object,
        supplier_invoices: Object,
        search: Object,
        activity_logs: Array
    },
    data() {
        return {
            showErrors: false,
            emitter: emitter,
            openModal: false,
            form: useForm({
                search: this.search.search
            })
        }
    },
    created() {
        this.moment = moment
    },
    methods: {
        deleteSupplierInvoice(id) {
            this.$swal.fire({
                title: 'Remove Supplier Invoice',
                html: "Removing this supplier invoice will delete this entry and any asscoiations made to customer invoices attributed to this supplier invoice. Once this has been removed the information will no longer be available on the fixture.<br/><br/>" +
                    "Are you sure you wish to do this?",
                showCloseButton: true,
                showCancelButton: true,
                confirmButtonText: 'Yes',
                cancelButtonText: 'No'
            }).then((result) => {
                if (result.isConfirmed) {
                    // Delete the country
                    this.$inertia.delete(route('frontend.fixture.supplier-invoice.invoice.delete', [this.fixture.id, id]), {
                        preserveScroll: true
                    });
                }
            });
        },
        number_format(nStr) {
            nStr += '';
            let x = nStr.split('.');
            let x1 = x[0];
            let x2 = x.length > 1 ? '.' + x[1] : '';
            let rgx = /(\d+)(\d{3})/;
            while (rgx.test(x1)) {
                x1 = x1.replace(rgx, '$1' + ',' + '$2');
            }
            return x1 + x2;
        },
        reset() {
            this.form.search = null;
        },
    },
    watch: {
        form: {
            deep: true,
            handler: debounce(function () {
                this.$inertia.get(route('frontend.fixture.supplier-invoice.index', this.fixture.id), pickBy(this.form), {preserveState: true, preserveScroll: true})
            }, 500),
        },
    },
}
</script>
